import { useCallback } from "react";
import { usePostHog } from "@posthog/react";

/** Which Arrivals screen an event came from. */
export type ArrivalsSource = "nearby" | "favorites";

/**
 * Every analytics event the app emits, with its properties, named in one place
 * so call sites can't drift on spelling or casing and reviewers can see the
 * taxonomy at a glance. Property names are snake_case.
 */
export interface AnalyticsEvents {
  /** User granted location from the landing (#119). */
  location_prompt_granted: undefined;
  /** User took the Escape Hatch instead (#118). */
  location_prompt_escaped: undefined;
  /** A Favorite's stored routeId moved to its Canonical Route Id on load (#159). */
  favorite_route_resolved: {
    from_route_id: string;
    to_route_id: string;
    /** The rewritten Favorite collided with another and was merged. */
    merged: boolean;
  };
  /** Rider removed a Favorite (#180). */
  favorite_removed: { stop_id: string; route_id: string; direction_id: number };
  /**
   * Rider saved a Favorite from an Arrivals screen. Only `nearby` today:
   * Favorites Arrivals can't save (#218).
   */
  favorite_saved: {
    stop_id: string;
    route_id: string;
    direction_id: number;
    source: ArrivalsSource;
  };
  /** An Arrivals screen first rendered with data (#219). */
  arrivals_viewed: {
    source: ArrivalsSource;
    agency: string;
    route_short_name: string;
    direction_id: number;
    /** Nearby only, rounded to 10 m. Never raw coordinates. */
    distance_to_stop_m?: number;
    destinations_count: number;
    has_live: boolean;
  };
  /** Rider chose a destination chip on an Arrivals screen (#219). */
  arrivals_destination_filtered: {
    destinations_count: number;
    /** 0 is the most frequent destination. */
    chosen_rank: number;
  };
}

export type AnalyticsEventName = keyof AnalyticsEvents;

/** An event's properties argument: none for an event without properties. */
type PropertiesArg<E extends AnalyticsEventName> =
  AnalyticsEvents[E] extends undefined ? [] : [properties: AnalyticsEvents[E]];

/**
 * Thin, spy-able wrapper over PostHog.
 *
 * Features capture events through this hook rather than reaching into
 * `@posthog/react` directly, which gives tests a single seam to mock (spy the
 * wrapper, assert the event fired) and keeps event names and properties typed.
 * `usePostHog` can return `undefined` outside a `PostHogProvider` (e.g.
 * Storybook), so capture is a guarded no-op in that case (the context default
 * is an uninitialized posthog-js instance).
 */
export function useAnalytics() {
  const posthog = usePostHog();

  const capture = useCallback(
    <E extends AnalyticsEventName>(event: E, ...[properties]: PropertiesArg<E>): void => {
      posthog.capture(event, properties);
    },
    [posthog],
  );

  return { capture };
}
